import React, { forwardRef, useMemo, useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
    FiAlertTriangle, FiArrowDown, FiArrowUp,
    FiChevronDown, FiEdit3, FiEye,
    FiFileText, FiSearch, FiTrash2, FiX, FiCheckCircle, FiXCircle
} from "react-icons/fi";
import config from "../../../config";

/* 1. DESIGN TOKENS */
export const T = {
    radius: "12px",
    radiusSm: "8px",
    border: "#d1d5db",
    borderStrong: "#9ca3af",
    bg: "#f7f9fc",
    cardBg: "#ffffff",
    textPrimary: "#0f172a",
    textMuted: "#64748b",
    textLight: "#94a3b8",
    accent: "#2563eb",
    success: "#059669",
    warning: "#d97706",
    danger: "#dc2626",
    shadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
};

/* 2. DOC STATUS BADGE */
const STATUS_STYLES = {
    Draft: { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
    "Pending for Approval": { bg: "#fffbeb", color: "#b45309", border: "#fcd34d" },
    Approved: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    Accepted: { bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
    Rejected: { bg: "#fef2f2", color: "#b91c1c", border: "#fca5a5" },
    Closed: { bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
    Shipping: { bg: "#eff6ff", color: "#2563eb", border: "#93c5fd" },
    Shipped: { bg: "#eff6ff", color: "#1d4ed8", border: "#93c5fd" },
    Delivered: { bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
    Cancelled: { bg: "#fef2f2", color: "#b91c1c", border: "#fca5a5" },
    Unpaid: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
    Overdue: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
    Paid: { bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
    Issued: { bg: "#eff6ff", color: "#1d4ed8", border: "#93c5fd" },
    Applied: { bg: "#fdf4ff", color: "#7e22ce", border: "#d8b4fe" },
    Void: { bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" },
    Partial: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
};

export function DocStatusBadge({ value = "Draft" }) {
    const c = STATUS_STYLES[value] ?? STATUS_STYLES.Draft;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: c.bg, color: c.color,
            border: `1px solid ${c.border}`,
            borderRadius: 999, padding: "3px 10px",
            fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
        }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.color, flexShrink: 0 }} />
            {value}
        </span>
    );
}

/* 3. NUMBER / CURRENCY HELPERS */

/** Round to 2 decimal places, avoiding floating-point drift. */
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Format a number as Malaysian Ringgit: "RM 1,234.56" */
export const fmtMYR = (n) =>
    "RM " + (Number(n) || 0).toLocaleString("en-MY", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

/** Format with an arbitrary currency symbol. Falls back to the code itself. */
export const fmtCurrency = (n, currency = "MYR") => {
    const symbol = currency === "MYR" ? "RM" : currency;
    return (
        symbol +
        " " +
        (Number(n) || 0).toLocaleString("en-MY", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
    );
};

/* 4. ROW PERMISSIONS */
const INTERMEDIATE_STATUSES = ["Approved", "Shipping", "Unpaid", "Issued"];
const TERMINAL_STATUSES = ["Accepted", "Delivered", "Paid", "Applied", "Cancelled", "Void", "Overdue"];

/**
 * Returns edit / delete / view-only flags for a given document status.
 * Used by both SalesDocList (row-level) and PreviewPanel.
 */
export function rowPerms(status) {
    const canEdit = status === "Draft" || status === "Rejected" || status === "Approved";
    const isIntermediate = INTERMEDIATE_STATUSES.includes(status);
    const isTerminal = TERMINAL_STATUSES.includes(status);
    return {
        canEdit,
        canDelete: !isTerminal,
        viewOnly: !canEdit && !isIntermediate,
        isArchive: status === "Rejected",
    };
}

/* 5. STAT CARD */
export function StatCard({ icon: Icon, color, label, value }) {
    return (
        <div style={{
            background: T.cardBg, border: `1px solid ${T.border}`,
            borderRadius: T.radius, padding: "14px 18px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            display: "flex", alignItems: "center", gap: 14,
        }}>
            <div style={{
                width: 40, height: 40, borderRadius: "10px",
                color, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Icon size={20} />
            </div>
            <div>
                <div style={{ fontSize: 12, color: T.textLight, marginBottom: 1 }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: T.textPrimary, lineHeight: 1 }}>
                    {value ?? 0}
                </div>
            </div>
        </div>
    );
}

/* 6. SORTABLE TABLE HEADER */
export function SortTh({ label, id, sortBy, sortDir, onSort }) {
    const active = sortBy === id;
    return (
        <th className="cl-th-sortable" onClick={() => onSort(id)}>
            {label}
            <span className={`cl-sort-icon${active ? " active" : ""}`}>
                {active
                    ? sortDir === "asc"
                        ? <FiArrowUp size={12} />
                        : <FiArrowDown size={12} />
                    : <span style={{ fontSize: 11 }}>⇅</span>
                }
            </span>
        </th>
    );
}

/* 7. SEARCH TYPE DROPDOWN (portal) */
export function SearchTypeDropdown({ anchorRef, open, current, options, onSelect }) {
    const [pos, setPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!open || !anchorRef.current) return;
        const r = anchorRef.current.getBoundingClientRect();
        setPos({
            top: r.bottom + window.scrollY + 4,
            left: r.right + window.scrollX - 210,
        });
    }, [open, anchorRef]);

    if (!open) return null;

    return ReactDOM.createPortal(
        <div className="cl-type-dropdown-portal" style={{ top: pos.top, left: pos.left, width: 210 }}>
            {options.map(({ key, label }) => (
                <div
                    key={key}
                    className={`cl-type-dropdown-option${current === key ? " selected" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); onSelect(key); }}
                >
                    {label}
                </div>
            ))}
        </div>,
        document.body
    );
}

/* 8. PREVIEW PANEL */
export function PreviewPanel({
    item,
    docType,
    previewFields,
    onClose,
    onView,
    onDelete,
    isInactive = false,
    renderPreviewActions = null,
}) {
    const p = rowPerms(item.status);
    const disableDelete = isInactive || !p.canDelete;
    const isDO = docType === "Delivery Order";

    return (
        <div className="cl-preview-panel">
            {/* Header */}
            <div className="cl-preview-header">
                <div style={{
                    width: 38, height: 38, borderRadius: "10px",
                    background: "#eff6ff", color: T.accent,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                    <FiFileText size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: "0.06em" }}>
                        {docType.toUpperCase()} PREVIEW
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, marginTop: 1 }}>
                        {item.id}
                    </div>
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 1 }}>
                        {item.customer || item.supplier || "—"}
                    </div>
                </div>
                <button
                    onClick={onClose}
                    style={{ background: "none", border: "none", color: T.textLight, cursor: "pointer", padding: 4, borderRadius: 4 }}
                >
                    <FiX size={15} />
                </button>
            </div>

            {/* Body */}
            <div className="cl-preview-body">
                {/* Status */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${T.border}` }}>
                    <DocStatusBadge value={item.status} />
                </div>

                {/* Dynamic fields */}
                {previewFields.map(({ icon: Icon, label, key, render }) => (
                    <div
                        key={label}
                        style={{ display: "flex", alignItems: "flex-start", gap: 9, padding: "7px 0", borderBottom: `1px solid ${T.bg}` }}
                    >
                        <div style={{
                            width: 24, height: 24, borderRadius: 6,
                            background: T.bg, color: T.textMuted, flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <Icon size={11} />
                        </div>
                        <div>
                            <div style={{ fontSize: 10, color: T.textLight, fontWeight: 500 }}>{label}</div>
                            <div style={{ fontSize: 13, color: T.textPrimary, fontWeight: 500 }}>
                                {render ? render(item[key], item) : item[key] || "—"}
                            </div>
                        </div>
                    </div>
                ))}

                {/* Line items mini-list */}
                {item.items?.length > 0 ? (
                    <div style={{ marginTop: 10, padding: "8px 10px", background: T.bg, borderRadius: T.radiusSm }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Items ({item.items.length})
                        </div>
                        {item.items.slice(0, 3).map((it, i) => (
                            <div
                                key={i}
                                style={{ display: "flex", fontSize: 12, color: T.textPrimary, padding: "3px 0", borderBottom: i < Math.min(item.items.length, 3) - 1 ? `1px solid ${T.border}` : "none" }}
                            >
                                <span style={{ color: T.textMuted, flexShrink: 0, marginRight: 6 }}>{i + 1}.</span>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {it.description || it.itemCode || "—"}
                                </span>
                                {!isDO && (
                                    <span style={{ fontWeight: 600, color: T.accent, flexShrink: 0, marginLeft: 8 }}>
                                        {fmtMYR((Number(it.qty) || 0) * (Number(it.unitPrice) || 0))}
                                    </span>
                                )}
                            </div>
                        ))}
                        {item.items.length > 3 && (
                            <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>
                                +{item.items.length - 3} more item(s)
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ marginTop: 10, padding: "8px 10px", background: T.bg, borderRadius: T.radiusSm, fontSize: 12, color: T.textMuted }}>
                        No line items
                    </div>
                )}

                {item.notes && (
                    <div style={{ marginTop: 8, padding: "8px 10px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: T.radiusSm, fontSize: 12, color: "#92400e" }}>
                        <strong>Notes:</strong> {item.notes}
                    </div>
                )}
                {item.rejectionReason && (
                    <div style={{ marginTop: 8, padding: "8px 10px", background: "#fef2f2", border: `1px solid ${T.danger}40`, borderRadius: T.radiusSm, fontSize: 12, color: T.danger }}>
                        <strong>Rejection Reason:</strong> {item.rejectionReason}
                    </div>
                )}
            </div>

            {/* Footer actions */}
            <div className="cl-preview-footer">
                {renderPreviewActions ? renderPreviewActions(item, onClose, onView) : (
                    <>
                        <button
                            className="cl-preview-btn-primary"
                            onClick={() => onView(item._basePath ? `${item._basePath}/${item.id}` : item.id)}
                        >
                            {p.canEdit ? <FiEdit3 size={12} /> : <FiEye size={12} />}
                            {" "}{p.canEdit ? "View / Edit" : "View"}
                        </button>
                        <button
                            className="cl-preview-btn-secondary"
                            onClick={() => onDelete(item)}
                            disabled={disableDelete}
                            style={{
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                                width: "100%", padding: "9px 12px", borderRadius: 8,
                                border: `1.5px solid ${disableDelete ? T.border : T.danger}`,
                                background: "#fff",
                                color: disableDelete ? T.textLight : T.danger,
                                fontSize: 13, fontWeight: 600,
                                opacity: disableDelete ? 0.7 : 1,
                                cursor: disableDelete ? "not-allowed" : "pointer",
                                transition: "background 0.15s, color 0.15s, border-color 0.15s",
                            }}
                            onMouseEnter={(e) => { if (!disableDelete) e.currentTarget.style.background = "#fef2f2"; }}
                            onMouseLeave={(e) => { if (!disableDelete) e.currentTarget.style.background = "#fff"; }}
                        >
                            <FiTrash2 size={12} /> Delete
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

/* 9. DELETE MODAL (portal) */
export function DeleteModal({ target, docType, onConfirm, onCancel }) {
    if (!target) return null;
    return ReactDOM.createPortal(
        <div className="cl-modal-backdrop" onClick={onCancel}>
            <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
                <button className="cl-modal-close" onClick={onCancel}><FiX size={14} /></button>
                <div className="cl-modal-icon"><FiTrash2 size={26} /></div>
                <p className="cl-modal-msg">
                    Delete <strong>{docType}</strong> <strong>{target.id}</strong>?
                </p>
                <div className="cl-modal-warning">
                    <div className="cl-modal-warning-title"><FiAlertTriangle size={12} /> Warning</div>
                    <div className="cl-modal-warning-text">This action cannot be undone.</div>
                </div>
                <div className="cl-modal-actions">
                    <button className="cl-modal-btn-confirm" onClick={onConfirm}>Yes, Delete</button>
                    <button className="cl-modal-btn-cancel" onClick={onCancel}>Cancel</button>
                </div>
            </div>
        </div>,
        document.body
    );
}

/* 10. APPROVE MODAL */
export function ApproveModal({ target, docType, onConfirm, onCancel }) {
    if (!target) return null;
    return ReactDOM.createPortal(
        <div className="cl-modal-backdrop" onClick={onCancel}>
            <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
                <button className="cl-modal-close" onClick={onCancel}><FiX size={14} /></button>
                <div className="cl-modal-icon" style={{ background: "#e6f7ee", color: T.success }}>
                    <FiCheckCircle size={26} />
                </div>
                <p className="cl-modal-msg">
                    Approve <strong>{docType}</strong> <strong>{target.id}</strong>?
                </p>
                <div className="cl-modal-actions">
                    <button
                        className="cl-modal-btn-confirm cl-modal-btn-confirm-success"
                        onClick={onConfirm}
                    >
                        Approve
                    </button>
                    <button className="cl-modal-btn-cancel" onClick={onCancel}>Cancel</button>
                </div>
            </div>
        </div>,
        document.body
    );
}

/* 11. REJECT MODAL */
export function RejectModal({ target, docType, onConfirm, onCancel }) {
    const [reason, setReason] = useState("");

    useEffect(() => {
        if (target) setReason(""); // reset each time a new item is opened
    }, [target]);

    if (!target) return null;

    const handleSubmit = () => {
        if (!reason.trim()) return; // simple guard, button is disabled anyway
        onConfirm(reason.trim());
    };

    return ReactDOM.createPortal(
        <div className="cl-modal-backdrop" onClick={onCancel}>
            <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
                <button className="cl-modal-close" onClick={onCancel}><FiX size={14} /></button>
                <div className="cl-modal-icon" style={{ background: "#fdecec", color: T.danger }}>
                    <FiXCircle size={26} />
                </div>
                <p className="cl-modal-msg">
                    Reject <strong>{docType}</strong> <strong>{target.id}</strong>?
                </p>
                <textarea
                    autoFocus
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason for rejection (required)"
                    rows={3}
                    className="cl-modal-textarea"
                    style={{ width: "100%", resize: "none", marginTop: 8, marginBottom: 14 }}
                />
                <div className="cl-modal-actions">
                    <button
                        className="cl-modal-btn-confirm"
                        style={{ background: T.danger, opacity: reason.trim() ? 1 : 0.5 }}
                        disabled={!reason.trim()}
                        onClick={handleSubmit}
                    >
                        Submit
                    </button>
                    <button className="cl-modal-btn-cancel" onClick={onCancel}>Cancel</button>
                </div>
            </div>
        </div>,
        document.body
    );
}

/* 12. SECTION HEADING */
export function SectionHeading({ icon: Icon, title }) {
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 8,
            paddingBottom: 10, marginBottom: 14,
            borderBottom: `1px solid ${T.border}`,
        }}>
            <div style={{
                width: 24, height: 24, borderRadius: "6px",
                background: "#eff6ff", color: T.accent,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Icon size={12} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>{title}</span>
        </div>
    );
}

/* 13. INFO ROW / DETAIL ROW / DETAIL SECTION HEADING */
export function InfoRow({ icon: Icon, label, children }) {
    return (
        <div style={{
            display: "flex", alignItems: "flex-start", gap: 12,
            padding: "10px 0", borderBottom: `1px solid ${T.bg}`,
        }}>
            {Icon && (
                <div style={{
                    width: 28, height: 28, borderRadius: "7px",
                    background: T.bg, color: T.textMuted, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Icon size={13} />
                </div>
            )}
            <div>
                <div style={{ fontSize: 11, color: T.textLight, fontWeight: 500, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 14, color: T.textPrimary, fontWeight: 500 }}>{children || "—"}</div>
            </div>
        </div>
    );
}

export function DetailRow({ icon: Icon, label, children }) {
    return (
        <div style={{
            display: "flex", alignItems: "flex-start", gap: 14,
            padding: "10px 0", borderBottom: `1px solid ${T.border}`,
        }}>
            <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: T.bg, color: T.textMuted, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Icon size={14} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: T.textLight, fontWeight: 500, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 14, color: T.textPrimary, fontWeight: 500 }}>{children || "—"}</div>
            </div>
        </div>
    );
}

export function DetailSectionHeading({ icon: Icon, title }) {
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 8,
            marginBottom: 4, paddingBottom: 10,
            borderBottom: `1.5px solid ${T.border}`,
        }}>
            <Icon size={15} color={T.accent} />
            <span style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary }}>{title}</span>
        </div>
    );
}

/* 14. BUTTON STYLE CONSTANTS */
export const btnStyleOutline = {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    height: 36, padding: "0 14px", boxSizing: "border-box",
    borderRadius: T.radiusSm,
    border: `1.5px solid ${T.border}`,
    background: "#fff", color: T.textMuted,
    fontSize: 13, fontWeight: 500, cursor: "pointer",
    lineHeight: 1,
    whiteSpace: "nowrap",
};

export const btnStylePrimary = {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    height: 36, padding: "0 14px", boxSizing: "border-box",
    borderRadius: T.radiusSm,
    border: "none", background: T.accent, color: "#fff",
    fontSize: 13, fontWeight: 600, cursor: "pointer",
    boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
    lineHeight: 1,
    whiteSpace: "nowrap",
};

export const btnStyleDanger = {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    height: 36, padding: "0 14px", boxSizing: "border-box",
    borderRadius: T.radiusSm,
    border: "none", background: T.danger, color: "#fff",
    fontSize: 13, fontWeight: 600, cursor: "pointer",
    lineHeight: 1,
    whiteSpace: "nowrap",
};

/* 15. ITEM SEARCH SELECT (async inventory lookup for line items) */
export function ItemSearchSelect({
    value,
    onSelect,
    onChange,
    placeholder = "Type part number...",
    error,
    searchSource,
    currencySettings = null,
    docCurrency = "MYR",
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [focused, setFocused] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const wrapRef = useRef(null);

    // Debounced API search
    useEffect(() => {
        if (!query || query.length < 2) { setResults([]); return; }

        const timer = setTimeout(() => {
            setSearching(true);
            const url = new URL(`${config.apiUrl}/searchInventoryItems`);
            url.searchParams.append("q", query);
            if (searchSource) url.searchParams.append("type", searchSource);

            fetch(url.toString())
                .then((r) => r.json())
                .then((data) => setResults(Array.isArray(data) ? data : []))
                .catch(() => setResults([]))
                .finally(() => setSearching(false));
        }, 400);

        return () => clearTimeout(timer);
    }, [query, searchSource]);

    const borderColor = error ? T.danger : focused ? T.accent : T.border;

    const recalcPos = () => {
        if (!wrapRef.current) return;
        const r = wrapRef.current.getBoundingClientRect();
        setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };

    return (
        <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
            <input
                type="text"
                autoComplete="off"
                value={open ? query : value || ""}
                placeholder={placeholder}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                    if (onChange) onChange(e.target.value);
                }}
                onFocus={() => {
                    setFocused(true);
                    setOpen(true);
                    setQuery("");
                    recalcPos();
                    window.addEventListener("scroll", recalcPos, true);
                    window.addEventListener("resize", recalcPos);
                    wrapRef.current._cleanup = () => {
                        window.removeEventListener("scroll", recalcPos, true);
                        window.removeEventListener("resize", recalcPos);
                    };
                }}
                onBlur={() => {
                    setFocused(false);
                    wrapRef.current?._cleanup?.();
                    setTimeout(() => setOpen(false), 200);
                }}
                style={{
                    width: "100%", height: 30, padding: "0 8px",
                    fontSize: 12, color: T.textPrimary, background: "#fff",
                    border: `1.5px solid ${borderColor}`, borderRadius: 6,
                    outline: "none", boxSizing: "border-box",
                    transition: "border-color 0.15s",
                }}
            />

            {open && (query.length >= 2 || results.length > 0) && ReactDOM.createPortal(
                <div style={{
                    position: "fixed", top: dropdownPos.top, left: dropdownPos.left,
                    width: dropdownPos.width > 300 ? dropdownPos.width : 350, maxWidth: "90vw",
                    zIndex: 9999, background: "#fff", border: `1px solid ${T.border}`,
                    borderRadius: 8, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)",
                    maxHeight: 260, overflowY: "auto", padding: "4px 0",
                }}>
                    {searching && (
                        <div style={{ padding: "12px 16px", fontSize: 13, color: T.textMuted, display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 14, height: 14, border: "2px solid #e2e8f0", borderTopColor: T.accent, borderRadius: "50%", animation: "cl-spin 0.6s linear infinite" }} />
                            Searching...
                        </div>
                    )}
                    {!searching && results.length === 0 && query.length >= 2 && (
                        <div style={{ padding: "12px 16px", fontSize: 13, color: T.textLight, fontStyle: "italic" }}>
                            No matches for "{query}"
                        </div>
                    )}
                    {results.map((item, i) => (
                        <div
                            key={i}
                            onMouseDown={() => { onSelect(item); setOpen(false); setQuery(item.itemCode); }}
                            style={{ padding: "10px 16px", cursor: "pointer", borderBottom: i < results.length - 1 ? "1px solid #f1f5f9" : "none", background: "#fff", transition: "background 0.15s" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>{item.itemCode}</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: T.accent, background: "#eff6ff", padding: "2px 6px", borderRadius: 4, textTransform: "uppercase" }}>
                                    {item.source}
                                </span>
                            </div>
                            <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.4 }}>{item.description}</div>
                            {item.unitPrice > 0 && (
                                <div style={{ fontSize: 11, fontWeight: 600, color: T.success, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                                    <span style={{ fontSize: 10, color: T.textLight, fontWeight: 400 }}>Original Price:</span>
                                    {fmtCurrency(item.unitPrice, item.currency)}
                                    {item.currency !== docCurrency && currencySettings && (() => {
                                        const fromRate = item.rate || 1.0;
                                        const toCurrencyObj = (currencySettings.currencies || []).find((c) => c.code === docCurrency);
                                        const toRate = toCurrencyObj ? toCurrencyObj.rate : 1.0;
                                        return (
                                            <>
                                                <span style={{ fontSize: 10, color: T.textLight, fontWeight: 400, marginLeft: 8 }}>
                                                    Converted ({docCurrency}):
                                                </span>
                                                <span style={{ color: T.accent }}>
                                                    {fmtCurrency((item.unitPrice * toRate) / fromRate, docCurrency)}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    ))}
                    <style>{`@keyframes cl-spin { to { transform: rotate(360deg); } }`}</style>
                </div>,
                document.body
            )}
            {error && <div style={{ fontSize: 10, color: T.danger, marginTop: 2 }}>{error}</div>}
        </div>
    );
}

/* 16. FIELD INPUT  (text / date / textarea) */
export function FieldInput({
    label, name, value, onChange,
    type = "text", as, rows,
    placeholder, required, error,
    readOnly = false, min,
}) {
    const [focused, setFocused] = useState(false);
    const borderColor = error ? T.danger : focused ? T.accent : T.border;

    const baseStyle = {
        width: "100%", fontSize: 13, color: T.textPrimary,
        background: readOnly ? T.bg : "#fff",
        fontFamily: "inherit",
        border: `1.5px solid ${borderColor}`,
        borderRadius: T.radiusSm, outline: "none",
        boxShadow: focused && !error ? "0 0 0 3px rgba(37,99,235,0.10)" : "none",
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxSizing: "border-box",
        cursor: readOnly ? "default" : "text",
    };

    const sharedProps = {
        name,
        value: value ?? "",
        onChange,
        placeholder,
        readOnly,
        min,
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
    };

    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>
                {label}{required && <span style={{ color: T.danger }}> *</span>}
            </label>
            {as === "textarea"
                ? <textarea rows={rows ?? 3} {...sharedProps} style={{ ...baseStyle, padding: "9px 12px", resize: "vertical", display: "block" }} />
                : <input type={type} {...sharedProps} style={{ ...baseStyle, height: 36, padding: "0 11px", display: "block" }} />
            }
            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{error}</div>}
        </div>
    );
}

/* 17. SEARCHABLE SELECT */

export function SearchableSelect({
    label, name, value, onChange,
    options = [], placeholder,
    required, error,
    allowFreeText = false,
    disabled = false,
    /* Compact mode: no label, smaller height/font/icon — for use inside table cells */
    compact = false,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [focused, setFocused] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const wrapRef = useRef(null);

    const filtered = useMemo(
        () => options.filter((o) => o.toLowerCase().includes((query || "").toLowerCase())),
        [options, query]
    );

    const borderColor = error ? T.danger : focused ? T.accent : T.border;
    const isCustomEntry = allowFreeText && query.trim() && !options.some((o) => o.toLowerCase() === query.trim().toLowerCase());

    const handleSelect = (opt) => {
        setQuery(opt);
        setOpen(false);
        setFocused(false);
        onChange({ target: { name, value: opt } });
    };

    const recalcPos = () => {
        if (!wrapRef.current) return;
        const r = wrapRef.current.getBoundingClientRect();
        setDropdownPos({ top: r.bottom + 2, left: r.left, width: r.width });
    };

    return (
        <div style={{ marginBottom: compact ? 0 : 14, position: "relative" }}>
            {!compact && (
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>
                    {label}{required && <span style={{ color: T.danger }}> *</span>}
                </label>
            )}
            <div ref={wrapRef} style={{ position: "relative" }}>
                <input
                    type="text"
                    autoComplete="off"
                    value={open ? query : value || ""}
                    placeholder={placeholder || (compact ? "" : `Select ${label}…`)}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={(e) => {
                        setFocused(true);
                        setOpen(true);
                        setQuery("");
                        recalcPos();
                        if (compact) e.target.select();
                        window.addEventListener("scroll", recalcPos, true);
                        window.addEventListener("resize", recalcPos);
                        wrapRef.current._cleanup = () => {
                            window.removeEventListener("scroll", recalcPos, true);
                            window.removeEventListener("resize", recalcPos);
                        };
                    }}
                    onBlur={() => {
                        setFocused(false);
                        wrapRef.current?._cleanup?.();
                        setTimeout(() => setOpen(false), 200);
                    }}
                    disabled={disabled}
                    style={compact ? {
                        width: "100%", border: `1.5px solid ${borderColor}`,
                        borderRadius: 6, padding: "5px 26px 5px 8px", fontSize: 12,
                        color: T.textPrimary, background: disabled ? T.bg : "#fff", outline: "none",
                        boxSizing: "border-box", transition: "border-color 0.15s",
                        opacity: disabled ? 0.5 : 1,
                        cursor: disabled ? "not-allowed" : "text",
                    } : {
                        width: "100%", height: 36, padding: "0 30px 0 11px",
                        fontSize: 13, color: T.textPrimary,
                        border: `1.5px solid ${borderColor}`,
                        borderRadius: T.radiusSm, outline: "none",
                        background: disabled ? T.bg : "#fff",
                        boxSizing: "border-box",
                        transition: "border-color 0.15s",
                        opacity: disabled ? 0.5 : 1,
                        cursor: disabled ? "not-allowed" : "text",
                    }}
                />
                <span style={{
                    position: "absolute", right: compact ? 7 : 9, top: "50%",
                    transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
                    pointerEvents: "none", transition: "transform 0.2s ease",
                    display: "inline-flex", alignItems: "center", color: T.textLight,
                }}>
                    <FiChevronDown size={compact ? 12 : 14} />
                </span>
            </div>
            {error && (
                <div style={{ fontSize: compact ? 10 : 11, color: T.danger, marginTop: compact ? 2 : 4, lineHeight: 1.2, whiteSpace: compact ? "normal" : "nowrap", wordBreak: compact ? "break-word" : "normal", maxWidth: compact ? 140 : "none" }}>
                    {error}
                </div>
            )}

            {open && !disabled && (filtered.length > 0 || isCustomEntry) && ReactDOM.createPortal(
                <div style={{
                    position: "fixed", top: dropdownPos.top, left: dropdownPos.left,
                    width: dropdownPos.width,
                    background: "#fff", border: `1px solid ${T.border}`,
                    borderRadius: T.radiusSm, boxShadow: "0 8px 24px -4px rgba(0,0,0,0.12)",
                    zIndex: 9999, maxHeight: 220, overflowY: "auto",
                }}>
                    {isCustomEntry && (
                        <div
                            onMouseDown={(e) => { e.preventDefault(); handleSelect(query.trim()); }}
                            style={{ padding: "9px 12px", fontSize: 13, cursor: "pointer", color: T.accent, fontWeight: 600, borderBottom: filtered.length ? `1px solid ${T.border}` : "none" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f9ff")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                            + Add &quot;{query.trim()}&quot;
                        </div>
                    )}
                    {filtered.map((opt) => (
                        <div
                            key={opt}
                            onMouseDown={() => handleSelect(opt)}
                            style={{
                                padding: "9px 12px", fontSize: 13, cursor: "pointer",
                                color: opt === value ? T.accent : T.textPrimary,
                                background: opt === value ? "#eff6ff" : "transparent",
                                fontWeight: opt === value ? 600 : 400,
                            }}
                            onMouseEnter={(e) => { if (opt !== value) e.currentTarget.style.background = "#f8fafc"; }}
                            onMouseLeave={(e) => { if (opt !== value) e.currentTarget.style.background = opt === value ? "#eff6ff" : "transparent"; }}
                        >
                            {opt}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
}

/* 18. SIMPLE SELECT  (native <select> with styled chevron) */

export function SimpleSelect({
    label, name, value, onChange,
    options = [], required, error,
    disabled = false, placeholder = "",
}) {
    const [focused, setFocused] = useState(false);
    const [open, setOpen] = useState(false);

    return (
        <div style={{ marginBottom: 14 }}>
            {label && (
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>
                    {label}{required && <span style={{ color: T.danger }}> *</span>}
                </label>
            )}
            <div style={{ position: "relative" }}>
                <select
                    name={name}
                    value={value ?? ""}
                    onChange={(e) => { onChange(e); setOpen(false); setFocused(false); }}
                    onFocus={() => { setFocused(true); setOpen(true); }}
                    onBlur={() => { setFocused(false); setOpen(false); }}
                    disabled={disabled}
                    style={{
                        width: "100%", height: 36, padding: "0 30px 0 11px",
                        fontSize: 13, color: T.textPrimary,
                        background: disabled ? T.bg : "#fff",
                        fontFamily: "inherit",
                        border: `1.5px solid ${focused ? T.accent : error ? T.danger : T.border}`,
                        borderRadius: T.radiusSm, outline: "none",
                        cursor: disabled ? "not-allowed" : "pointer",
                        appearance: "none", WebkitAppearance: "none",
                        boxSizing: "border-box",
                    }}
                >
                    {placeholder && <option value="">{placeholder}</option>}
                    {options.map((o) => (
                        <option key={o.value ?? o} value={o.value ?? o}>
                            {o.label ?? o}
                        </option>
                    ))}
                </select>
                <span style={{
                    position: "absolute", right: 10, top: "50%",
                    transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
                    pointerEvents: "none", transition: "transform 0.2s ease",
                    display: "inline-flex", alignItems: "center", color: T.textLight,
                }}>
                    <FiChevronDown size={14} />
                </span>
            </div>
            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{error}</div>}
        </div>
    );
}

/* 19. BANNER  (success / danger / info) */

const BANNER_COLORS = {
    success: { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0" },
    danger: { bg: "#fef2f2", color: "#991b1b", border: "#fecaca" },
    info: { bg: "#eff6ff", color: "#1e40af", border: "#bfdbfe" },
};

export function Banner({ type, msg, onClose }) {
    if (!msg) return null;
    const c = BANNER_COLORS[type] ?? BANNER_COLORS.info;
    return (
        <div style={{
            padding: "11px 16px", borderRadius: T.radiusSm, marginBottom: 16,
            fontSize: 13, fontWeight: 500,
            background: c.bg, color: c.color, border: `1px solid ${c.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
            <span>{msg}</span>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0 }}>
                <FiX size={13} />
            </button>
        </div>
    );
}

/* 20. COMPANY STATUS BANNER */
export function CompanyStatusBanner({ isInactive, onNavigate }) {
    if (!isInactive) return null;
    return (
        <div style={{
            padding: "12px 18px", borderRadius: T.radiusSm, marginBottom: 20,
            fontSize: 13, fontWeight: 500,
            background: "#fffbeb", color: "#92400e",
            border: "1px solid #fce788",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FiAlertTriangle size={16} />
                <span>
                    <strong>Company Status is Inactive.</strong>{" "}
                    You cannot create, edit, or delete documents until the profile is activated.
                </span>
            </div>
            {onNavigate && (
                <button
                    onClick={onNavigate}
                    style={{
                        background: "#92400e", color: "#fff", border: "none",
                        padding: "5px 12px", borderRadius: 6,
                        fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                    }}
                >
                    Go to Profile
                </button>
            )}
        </div>
    );
}

/* 21. LINE ITEMS TABLE */
const COMMON_UNITS = ["pcs", "kg", "g", "m", "cm", "mm", "box", "carton", "set", "lot", "roll"];

export function LineItemsTable({
    items = [],
    onChange,
    readOnly = false,
    taxRate = 0,
    error,
    selectable = false,
    noPrice = false,
    showRestock = false,
    itemFieldErrors = {},
    subtotalLabel = "Subtotal",
    totalLabel = "Grand Total (RM)",
    forceNegativeTotals = false,
    searchable = false,
    searchSource,
    docType,
    currencySettings = null,
    docCurrency = "MYR",
    mode = "",
}) {
    const isPaymentVoucher = mode === "payment_voucher" || docType === "Payment Voucher";

    // ── Row operations ──────────────────────────────────────────────────────

    const addRow = () =>
        onChange([
            ...items,
            isPaymentVoucher
                ? { accountCode: "", description: "", payAmount: 0 }
                : { itemCode: "", description: "", unit: "", qty: 1, unitPrice: 0, selected: true, restock: false },
        ]);

    const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));
    const updateCell = (idx, field, value) => onChange(items.map((row, i) => i === idx ? { ...row, [field]: value } : row));
    const updateRow = (idx, newValues) => onChange(items.map((row, i) => i === idx ? { ...row, ...newValues } : row));

    // ── Totals ──────────────────────────────────────────────────────────────

    const { visibleItems, subtotal, taxAmount, grandTotal } = useMemo(() => {
        const active = selectable ? items.filter((r) => r.selected !== false) : items;

        if (isPaymentVoucher) {
            const total = active.reduce((sum, r) => sum + round2(Number(r.payAmount) || 0), 0);
            return { visibleItems: items, subtotal: total, taxAmount: 0, grandTotal: total };
        }

        const sub = active.reduce((sum, r) => sum + round2((Number(r.qty) || 0) * (Number(r.unitPrice) || 0)), 0);
        const tax = round2(sub * (taxRate / 100));
        const grand = round2(sub + tax);
        return { visibleItems: items, subtotal: sub, taxAmount: tax, grandTotal: grand };
    }, [items, selectable, taxRate, isPaymentVoucher]);

    const displaySubtotal = forceNegativeTotals ? -Math.abs(subtotal) : subtotal;
    const displayTaxAmount = forceNegativeTotals ? -Math.abs(taxAmount) : taxAmount;
    const displayGrandTotal = forceNegativeTotals ? -Math.abs(grandTotal) : grandTotal;

    // ── Column definitions ──────────────────────────────────────────────────

    const headers = useMemo(() => {
        if (isPaymentVoucher) {
            return [
                { label: "#", align: "center", width: "36px" },
                { label: "Account Code / Category", align: "left", width: "25%" },
                { label: "Description", align: "left", width: "auto" },
                { label: `Pay Amount (${docCurrency === "MYR" ? "RM" : docCurrency})`, align: "right", width: "18%" },
                ...(!readOnly ? [{ label: "", align: "center", width: "38px" }] : []),
            ];
        }
        return [
            ...(selectable ? [{ label: "", align: "center", width: "36px" }] : []),
            { label: "#", align: "center", width: "36px" },
            { label: "Item Code", align: "left", width: "14%" },
            { label: "Description", align: "left", width: "auto" },
            { label: "Unit", align: "left", width: "9%" },
            { label: "Qty", align: "right", width: "8%" },
            ...(!noPrice ? [
                { label: `Unit Price (${docCurrency === "MYR" ? "RM" : docCurrency})`, align: "right", width: "14%" },
                { label: `Subtotal (${docCurrency === "MYR" ? "RM" : docCurrency})`, align: "right", width: "14%" },
            ] : []),
            ...(showRestock ? [{ label: "Restock?", align: "center", width: "80px" }] : []),
            ...(!readOnly ? [{ label: "", align: "center", width: "38px" }] : []),
        ];
    }, [isPaymentVoucher, selectable, noPrice, showRestock, readOnly, docCurrency]);

    const colCount = headers.length;
    const labelColSpan = !readOnly ? headers.length - 2 : headers.length - 1;

    const cellStyle = {
        padding: "8px 10px", fontSize: 13,
        borderBottom: `1px solid ${T.border}`,
        verticalAlign: "middle",
    };

    /** Inline editable cell input */
    const editInput = (idx, field, val, type = "text", extraStyle = {}, errorMsg, listId = null) => (
        <div style={{ position: "relative" }}>
            <input
                type={type}
                value={val ?? ""}
                list={listId}
                onChange={(e) => updateCell(idx, field, e.target.value)}
                style={{
                    width: "100%", border: `1.5px solid ${errorMsg ? T.danger : T.border}`,
                    borderRadius: 6, padding: "5px 8px", fontSize: 12,
                    color: T.textPrimary, background: "#fff", outline: "none",
                    boxSizing: "border-box", transition: "border-color 0.15s", ...extraStyle,
                }}
                onFocus={(e) => { e.target.style.borderColor = T.accent; e.target.select(); }}
                onBlur={(e) => { e.target.style.borderColor = errorMsg ? T.danger : T.border; }}
            />
            {errorMsg && (
                <div style={{ fontSize: 10, color: T.danger, marginTop: 2, lineHeight: 1.2, whiteSpace: "normal", wordBreak: "break-word", maxWidth: 140 }}>
                    {errorMsg}
                </div>
            )}
        </div>
    );

    return (
        <div>
            <div style={{ overflow: "visible", borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
                    <colgroup>
                        {headers.map((h, i) => <col key={i} style={{ width: h.width }} />)}
                    </colgroup>

                    <thead>
                        <tr style={{ background: "#f8fafc" }}>
                            {headers.map((h, i) => (
                                <th key={i} style={{
                                    padding: "10px 10px", fontSize: 11, fontWeight: 700,
                                    color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em",
                                    borderBottom: `2px solid ${T.borderStrong}`, textAlign: h.align,
                                    whiteSpace: "nowrap", background: "#f8fafc",
                                }}>
                                    {h.label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={colCount} style={{ padding: "36px 10px", textAlign: "center", color: T.textLight, fontStyle: "italic", fontSize: 13, borderBottom: `1px solid ${T.border}` }}>
                                    {isPaymentVoucher
                                        ? "No line items yet."
                                        : "No items selected. Check items above to add to delivery."}
                                </td>
                            </tr>
                        )}

                        {visibleItems.map((row, idx) => {
                            const originalIdx = items.findIndex((item) => item === row);
                            const fe = itemFieldErrors[originalIdx] || {};
                            const rowSub = round2((Number(row.qty) || 0) * (Number(row.unitPrice) || 0));

                            return (
                                <tr
                                    key={originalIdx}
                                    style={{
                                        background: selectable && row.selected === false
                                            ? "#f8f8f8"
                                            : idx % 2 === 0 ? "#fff" : "#fafbfd",
                                        opacity: selectable && row.selected === false ? 0.45 : 1,
                                        transition: "background 0.1s",
                                    }}
                                >
                                    {/* Checkbox (selectable mode) */}
                                    {selectable && !isPaymentVoucher && (
                                        <td style={{ ...cellStyle, textAlign: "center" }}>
                                            <input
                                                type="checkbox"
                                                checked={!!row.selected}
                                                disabled={readOnly}
                                                onChange={readOnly ? undefined : (e) => updateCell(originalIdx, "selected", e.target.checked)}
                                                style={{ width: 15, height: 15, accentColor: T.accent, cursor: readOnly ? "default" : "pointer", opacity: readOnly ? 0.7 : 1 }}
                                            />
                                        </td>
                                    )}

                                    {/* Row number */}
                                    <td style={{ ...cellStyle, textAlign: "center", color: T.textLight, fontSize: 12, fontWeight: 600 }}>
                                        {idx + 1}
                                    </td>

                                    {/* Payment Voucher columns */}
                                    {isPaymentVoucher ? (
                                        <>
                                            <td style={cellStyle}>
                                                {readOnly
                                                    ? <span style={{ fontSize: 13, color: T.textMuted, fontFamily: "monospace" }}>{row.accountCode || "—"}</span>
                                                    : (
                                                        <select
                                                            value={row.accountCode || ""}
                                                            onChange={(e) => updateCell(originalIdx, "accountCode", e.target.value)}
                                                            style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, color: row.accountCode ? T.textPrimary : T.textLight, background: "#fff", outline: "none", boxSizing: "border-box" }}
                                                        >
                                                            <option value="">— Select category —</option>
                                                            {["Cost of Goods Sold", "Operating Expenses", "Administrative Expenses", "Rental & Utilities", "Salaries & Wages", "Marketing & Advertising", "Transportation & Logistics", "Professional Fees", "Maintenance & Repairs", "Others"].map((c) => (
                                                                <option key={c} value={c}>{c}</option>
                                                            ))}
                                                        </select>
                                                    )
                                                }
                                            </td>
                                            <td style={cellStyle}>
                                                {readOnly
                                                    ? <span style={{ fontSize: 13 }}>{row.description || "—"}</span>
                                                    : editInput(originalIdx, "description", row.description, "text", {}, fe.description)
                                                }
                                            </td>
                                            <td style={{ ...cellStyle, textAlign: "right" }}>
                                                {readOnly
                                                    ? <span style={{ fontWeight: 600 }}>{fmtCurrency(row.payAmount, docCurrency)}</span>
                                                    : editInput(originalIdx, "payAmount", row.payAmount ?? 0, "number", { textAlign: "right" }, fe.payAmount)
                                                }
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            {/* Item Code */}
                                            <td style={cellStyle}>
                                                {readOnly
                                                    ? <span style={{ fontSize: 13, color: T.textMuted, fontFamily: "monospace" }}>{row.itemCode || "—"}</span>
                                                    : searchable
                                                        ? (
                                                            <ItemSearchSelect
                                                                value={row.itemCode}
                                                                searchSource={searchSource}
                                                                currencySettings={currencySettings}
                                                                docCurrency={docCurrency}
                                                                onSelect={(sel) => {
                                                                    const fromRate = sel.rate || 1.0;
                                                                    const toCurrencyObj = (currencySettings?.currencies || []).find((c) => c.code === docCurrency);
                                                                    const toRate = toCurrencyObj ? toCurrencyObj.rate : 1.0;
                                                                    updateRow(originalIdx, {
                                                                        itemCode: sel.itemCode,
                                                                        description: sel.description,
                                                                        unitPrice: round2((sel.unitPrice * toRate) / fromRate),
                                                                        ...(sel.unit ? { unit: sel.unit } : {}),
                                                                    });
                                                                }}
                                                                onChange={(val) => updateCell(originalIdx, "itemCode", val)}
                                                                error={fe.itemCode}
                                                            />
                                                        )
                                                        : editInput(originalIdx, "itemCode", row.itemCode, "text", {}, fe.itemCode)
                                                }
                                            </td>

                                            {/* Description */}
                                            <td style={cellStyle}>
                                                {readOnly
                                                    ? <span style={{ fontSize: 13 }}>{row.description || "—"}</span>
                                                    : editInput(originalIdx, "description", row.description, "text", {}, fe.description)
                                                }
                                            </td>

                                            {/* Unit */}
                                            <td style={cellStyle}>
                                                {readOnly
                                                    ? <span style={{ fontSize: 13, color: T.textMuted }}>{row.unit || "—"}</span>
                                                    : (
                                                        <SearchableSelect
                                                            compact
                                                            name="unit"
                                                            value={row.unit}
                                                            onChange={(e) => updateCell(originalIdx, "unit", e.target.value)}
                                                            options={COMMON_UNITS}
                                                            allowFreeText
                                                            error={fe.unit}
                                                        />
                                                    )
                                                }
                                            </td>

                                            {/* Qty */}
                                            <td style={{ ...cellStyle, textAlign: "right" }}>
                                                {readOnly
                                                    ? <span style={{ fontWeight: 500 }}>{row.qty ?? 0}</span>
                                                    : editInput(originalIdx, "qty", row.qty ?? 0, "number", {}, fe.qty, { textAlign: "right" })
                                                }
                                            </td>

                                            {/* Price columns */}
                                            {!noPrice && (
                                                <>
                                                    <td style={{ ...cellStyle, textAlign: "right" }}>
                                                        {readOnly
                                                            ? <span>{fmtCurrency(row.unitPrice, docCurrency)}</span>
                                                            : editInput(originalIdx, "unitPrice", row.unitPrice, "number", {}, fe.unitPrice, { textAlign: "right" })
                                                        }
                                                    </td>
                                                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: 600, color: T.textPrimary }}>
                                                        {fmtCurrency(rowSub, docCurrency)}
                                                    </td>
                                                </>
                                            )}

                                            {/* Restock */}
                                            {showRestock && (
                                                <td style={{ ...cellStyle, textAlign: "center" }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!row.restock}
                                                        disabled={readOnly}
                                                        onChange={readOnly ? undefined : (e) => updateCell(originalIdx, "restock", e.target.checked)}
                                                        style={{ width: 15, height: 15, accentColor: T.accent, cursor: readOnly ? "default" : "pointer" }}
                                                    />
                                                </td>
                                            )}
                                        </>
                                    )}

                                    {/* Remove row */}
                                    {!readOnly && (
                                        <td style={{ ...cellStyle, textAlign: "center" }}>
                                            <button
                                                onClick={() => removeRow(originalIdx)}
                                                title="Remove row"
                                                style={{ background: "none", border: "none", color: T.textLight, cursor: "pointer", padding: 4, borderRadius: 4, display: "inline-flex", alignItems: "center", transition: "color 0.15s" }}
                                                onMouseEnter={(e) => (e.currentTarget.style.color = T.danger)}
                                                onMouseLeave={(e) => (e.currentTarget.style.color = T.textLight)}
                                            >
                                                <FiX size={14} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>

                    {/* Footer totals */}
                    {(!noPrice || isPaymentVoucher) && (
                        <tfoot>
                            {/* Subtotal row (only when SST applies) */}
                            {!isPaymentVoucher && taxRate > 0 && (
                                <tr>
                                    <td colSpan={labelColSpan} style={{ padding: "9px 10px", textAlign: "right", fontSize: 12, fontWeight: 500, color: T.textMuted, borderTop: `1px solid ${T.borderStrong}`, background: "#fafbfd" }}>
                                        {subtotalLabel}
                                    </td>
                                    <td style={{ padding: "9px 10px", textAlign: "right", fontSize: 13, fontWeight: 600, color: T.textPrimary, borderTop: `1px solid ${T.borderStrong}`, background: "#fafbfd", whiteSpace: "nowrap" }}>
                                        {fmtCurrency(displaySubtotal, docCurrency)}
                                    </td>
                                    {!readOnly && <td style={{ borderTop: `1px solid ${T.borderStrong}`, background: "#fafbfd" }} />}
                                </tr>
                            )}

                            {/* SST row */}
                            {!isPaymentVoucher && taxRate > 0 && (
                                <tr>
                                    <td colSpan={labelColSpan} style={{ padding: "9px 10px", textAlign: "right", fontSize: 12, fontWeight: 500, color: T.textMuted, background: "#fafbfd" }}>
                                        SST ({taxRate}%)
                                    </td>
                                    <td style={{ padding: "9px 10px", textAlign: "right", fontSize: 13, fontWeight: 600, color: T.textPrimary, background: "#fafbfd", whiteSpace: "nowrap" }}>
                                        {fmtCurrency(displayTaxAmount, docCurrency)}
                                    </td>
                                    {!readOnly && <td style={{ background: "#fafbfd" }} />}
                                </tr>
                            )}

                            {/* Grand total row */}
                            <tr>
                                <td colSpan={labelColSpan} style={{ padding: "11px 10px", textAlign: "right", fontSize: 12, fontWeight: 700, color: T.textPrimary, borderTop: `2px solid ${T.borderStrong}`, background: "#f0f4ff" }}>
                                    {totalLabel}
                                </td>
                                <td style={{ padding: "11px 10px", textAlign: "right", fontSize: 15, fontWeight: 700, color: T.accent, borderTop: `2px solid ${T.borderStrong}`, background: "#f0f4ff", whiteSpace: "nowrap" }}>
                                    {fmtCurrency(displayGrandTotal, docCurrency)}
                                </td>
                                {!readOnly && <td style={{ borderTop: `2px solid ${T.borderStrong}`, background: "#f0f4ff" }} />}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* Add row button */}
            {!readOnly && (
                <button
                    onClick={addRow}
                    style={{
                        marginTop: 10, display: "flex", alignItems: "center", gap: 6,
                        padding: "7px 14px", borderRadius: T.radiusSm,
                        border: `1.5px solid ${T.borderStrong}`,
                        background: "#fff", color: T.textMuted,
                        fontSize: 13, fontWeight: 600, cursor: "pointer",
                        transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; e.currentTarget.style.background = "#eff6ff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.borderStrong; e.currentTarget.style.color = T.textMuted; e.currentTarget.style.background = "#fff"; }}
                >
                    + Add Item Row
                </button>
            )}

            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 6 }}>{error}</div>}
        </div>
    );
}

/* 22. DISCARD MODAL */
export function DiscardModal({ open, onConfirm, onCancel }) {
    if (!open) return null;
    return (
        <div
            style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={onCancel}
        >
            <div
                style={{ background: "#fff", borderRadius: T.radius, padding: "28px 28px 22px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", width: 360, maxWidth: "90vw" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, marginBottom: 10 }}>Discard changes?</div>
                <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 22 }}>You have unsaved changes. Leaving now will lose them.</div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: T.radiusSm, border: `1.5px solid ${T.border}`, background: "#fff", color: T.textMuted, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                        Keep Editing
                    </button>
                    <button onClick={onConfirm} style={{ padding: "8px 18px", borderRadius: T.radiusSm, border: "none", background: T.danger, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                        Discard
                    </button>
                </div>
            </div>
        </div>
    );
}

/* 23. QUOTATION PRINT TEMPLATE */
const pS = {
    page: { fontFamily: "'Helvetica Neue','Helvetica','Arial',sans-serif", fontSize: 12, color: "#333", background: "#fff", padding: "30px 40px", boxSizing: "border-box", width: "210mm", minHeight: "297mm", margin: "0 auto", boxShadow: "0 0 10px rgba(0,0,0,0.15)" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px", paddingBottom: "10px", borderBottom: "2px solid #eee" },
    companyDetails: { flex: 1 },
    companyName: { fontWeight: 700, fontSize: 20, color: "#000", marginBottom: 2 },
    companyInfo: { fontSize: 11, color: "#555", lineHeight: 1.5 },
    quoteInfo: { textAlign: "right", minWidth: 200 },
    quoteTitle: { fontSize: 28, fontWeight: "bold", color: "#252f3f", marginBottom: "10px" },
    quoteMeta: { fontSize: 11, color: "#555" },
    customerSection: { marginTop: "15px", marginBottom: "15px" },
    billToHeader: { padding: "4px 8px", background: "#252f3f", color: "#fff", fontSize: 11, fontWeight: "bold", marginBottom: "8px" },
    customerDetails: { fontSize: 12, lineHeight: 1.6 },
    table: { width: "100%", borderCollapse: "collapse", marginTop: "15px", fontSize: 12 },
    th: { background: "#252f3f", color: "#fff", padding: "8px 8px", textAlign: "left", fontWeight: "bold", textTransform: "uppercase", fontSize: 9, letterSpacing: "0.5px" },
    td: { padding: "8px 8px", borderBottom: "1px solid #eee", verticalAlign: "middle" },
    remarks: { flex: 1, padding: "8px 10px", fontSize: 11, color: "#444", lineHeight: 1.5 },
    totalRow: { display: "flex", justifyContent: "space-between", padding: "5px 8px", borderBottom: "1px solid #eee", fontSize: 12 },
    totalLabel: { display: "table-cell", padding: "6px 10px", fontWeight: "bold", textAlign: "left" },
    grandTotalRow: { display: "flex", justifyContent: "space-between", padding: "8px 8px", background: "#252f3f", color: "#fff", fontWeight: "bold", fontSize: 12 },
    signatureSection: { display: "flex", justifyContent: "space-between", marginTop: "60px", paddingTop: "15px", borderTop: "1px solid #eee" },
    signatureBox: { width: "48%", fontSize: 10, color: "#555" },
    signatureLine: { borderTop: "1px solid #aaa", marginTop: "60px", paddingTop: "5px" },
    footer: { textAlign: "center", marginTop: "15px", fontSize: 9, color: "#888", fontStyle: "italic" },
};

const fmtPrintDate = (d) => {
    if (!d) return "—";
    const [year, month, day] = d.split("-");
    return year && month && day ? `${day}/${month}/${year}` : d;
};

export const QuotationPrintTemplate = forwardRef(function QuotationPrintTemplate(
    { data = {}, companyProfile = {}, docType = "Quotation", taxRate = 0, forceNegativeTotals = false },
    ref
) {
    const isDO = docType === "Delivery Order";
    const isPV = docType === "Payment Voucher";
    const cp = companyProfile || {};

    const items = useMemo(
        () => (data.items || []).filter((item) => item.selected !== false),
        [data.items]
    );

    const { subtotal, taxAmt, grandTotal } = useMemo(() => {
        if (isPV) {
            const total = items.reduce((s, r) => s + round2(Number(r.payAmount) || 0), 0);
            return { subtotal: total, taxAmt: 0, grandTotal: total };
        }
        const sub = items.reduce((s, r) => s + round2((Number(r.qty) || 0) * (Number(r.unitPrice) || 0)), 0);
        const tax = round2(sub * (taxRate / 100));
        const grand = round2(sub + tax);
        return { subtotal: sub, taxAmt: tax, grandTotal: grand };
    }, [items, taxRate, isPV]);

    const displaySubtotal = forceNegativeTotals ? -Math.abs(subtotal) : subtotal;
    const displayTaxAmount = forceNegativeTotals ? -Math.abs(taxAmt) : taxAmt;
    const displayGrandTotal = forceNegativeTotals ? -Math.abs(grandTotal) : grandTotal;

    const numRows = items.length;
    const minRows = 4;
    const emptyRows = useMemo(
        () => (numRows < minRows ? [...Array(minRows - numRows)] : []),
        [numRows]
    );

    return (
        <div ref={ref} className="print-page" style={pS.page}>
            <style>{`
                @media print {
                    @page { size: A4; margin: 10mm 15mm; }
                    .print-page {
                        box-shadow: none !important; margin: 0 !important;
                        padding: 0 !important; width: 100% !important;
                        max-width: 100% !important; min-height: unset !important;
                        height: auto !important; box-sizing: border-box !important;
                    }
                    header, section, table, footer { page-break-inside: avoid !important; break-inside: avoid !important; }
                }
            `}</style>

            {/* ── Company header ── */}
            <header style={pS.header}>
                <div style={pS.companyDetails}>
                    {cp.LogoUrl && <img src={cp.LogoUrl} alt="logo" style={{ maxHeight: 50, marginBottom: 10 }} />}
                    <div style={pS.companyName}>{cp.CompanyName || "[Company Name]"}</div>
                    <div style={pS.companyInfo}>
                        {cp.Address && <div style={{ whiteSpace: "pre-line" }}>{cp.Address}</div>}
                        {cp.PhoneNumber && <div>Tel: {cp.PhoneNumber}</div>}
                        {cp.Email && <div>Email: {cp.Email}</div>}
                        {cp.RegNo && <div>Reg No: {cp.RegNo}</div>}
                    </div>
                </div>
                <div style={pS.quoteInfo}>
                    <div style={pS.quoteTitle}>{docType.toUpperCase()}</div>
                    <table style={{ width: "100%" }}>
                        <tbody>
                            <tr>
                                <td style={{ ...pS.quoteMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>DATE</td>
                                <td style={{ ...pS.quoteMeta, textAlign: "right" }}>{fmtPrintDate(data.date)}</td>
                            </tr>
                            <tr>
                                <td style={{ ...pS.quoteMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>REF NO.</td>
                                <td style={{ ...pS.quoteMeta, textAlign: "right" }}>{data.id || "N/A"}</td>
                            </tr>
                            {data.validUntil && (
                                <tr>
                                    <td style={{ ...pS.quoteMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>VALID UNTIL</td>
                                    <td style={{ ...pS.quoteMeta, textAlign: "right" }}>{fmtPrintDate(data.validUntil)}</td>
                                </tr>
                            )}
                            {data.paymentTerms && docType !== "Credit Note" && (
                                <tr>
                                    <td style={{ ...pS.quoteMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>TERMS</td>
                                    <td style={{ ...pS.quoteMeta, textAlign: "right" }}>{data.paymentTerms}</td>
                                </tr>
                            )}
                            {(data.invoiceId || data.invoiceRef) && docType === "Credit Note" && (
                                <tr>
                                    <td style={{ ...pS.quoteMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>INVOICE REF.</td>
                                    <td style={{ ...pS.quoteMeta, textAlign: "right" }}>{data.invoiceId || data.invoiceRef}</td>
                                </tr>
                            )}
                            {(data.soRef || data.so_ref) && ["Invoice", "Delivery Order"].includes(docType) && (
                                <tr>
                                    <td style={{ ...pS.quoteMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>SO REF.</td>
                                    <td style={{ ...pS.quoteMeta, textAlign: "right" }}>{data.soRef || data.so_ref}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </header>

            {/* ── Bill-to / Ship-to ── */}
            <section style={pS.customerSection}>
                <div style={{ display: "flex", gap: "20px" }}>
                    <div style={{ flex: 1 }}>
                        <div style={pS.billToHeader}>BILL TO</div>
                        <div style={pS.customerDetails}>
                            <div style={{ fontWeight: "bold" }}>{data.customer || "—"}</div>
                            {data.customerAddress
                                ? <div style={{ whiteSpace: "pre-line" }}>{data.customerAddress}</div>
                                : <div style={{ color: "#999", fontStyle: "italic" }}>No billing address provided</div>}
                            {data.contactPerson && <div>Attn: {data.contactPerson}</div>}
                            {data.phoneNumber && <div>Tel: {data.phoneNumber}</div>}
                        </div>
                    </div>

                    {data.shippingAddress && (
                        <div style={{ flex: 1 }}>
                            <div style={pS.billToHeader}>SHIP TO</div>
                            <div style={pS.customerDetails}>
                                {data.shippingAddress
                                    ? <div style={{ whiteSpace: "pre-line" }}>{data.shippingAddress}</div>
                                    : <div style={{ color: "#999", fontStyle: "italic" }}>No shipping address provided</div>}
                                {data.contactPerson && <div>Attn: {data.contactPerson}</div>}
                                {data.phoneNumber && <div>Tel: {data.phoneNumber}</div>}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* ── Line items table ── */}
            <table style={pS.table}>
                <thead>
                    {isPV ? (
                        <tr>
                            <th style={{ ...pS.th, width: "5%", textAlign: "center" }}>#</th>
                            <th style={{ ...pS.th, width: "30%" }}>Account Code / Category</th>
                            <th style={{ ...pS.th, width: "45%" }}>Description</th>
                            <th style={{ ...pS.th, width: "20%", textAlign: "right" }}>
                                Pay Amount ({data.currency === "MYR" ? "RM" : data.currency || "RM"})
                            </th>
                        </tr>
                    ) : (
                        <tr>
                            <th style={{ ...pS.th, width: "30px", textAlign: "center" }}>#</th>
                            <th style={{ ...pS.th, width: "15%" }}>Item Code</th>
                            <th style={{ ...pS.th, width: "auto" }}>Description</th>
                            <th style={{ ...pS.th, width: "8%", textAlign: "center" }}>Unit</th>
                            <th style={{ ...pS.th, width: "8%", textAlign: "right" }}>Qty</th>
                            {!isDO && (
                                <>
                                    <th style={{ ...pS.th, width: "12%", textAlign: "right" }}>Unit Price</th>
                                    <th style={{ ...pS.th, width: "12%", textAlign: "right" }}>
                                        Amount ({data.currency === "MYR" ? "RM" : data.currency})
                                    </th>
                                </>
                            )}
                        </tr>
                    )}
                </thead>
                <tbody>
                    {isPV
                        ? items.map((r, i) => (
                            <tr key={i}>
                                <td style={{ ...pS.td, textAlign: "center", width: "5%" }}>{i + 1}</td>
                                <td style={{ ...pS.td, width: "30%" }}>{r.accountCode || "—"}</td>
                                <td style={{ ...pS.td, width: "45%", wordBreak: "break-word", whiteSpace: "normal" }}>{r.description || "—"}</td>
                                <td style={{ ...pS.td, textAlign: "right", fontWeight: "bold", width: "20%" }}>
                                    {Number(r.payAmount || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                            </tr>
                        ))
                        : items.filter((r) => !isDO || r.selected !== false).map((r, i) => (
                            <tr key={i}>
                                <td style={{ ...pS.td, textAlign: "center" }}>{i + 1}</td>
                                <td style={pS.td}>{r.itemCode || ""}</td>
                                <td style={pS.td}>{r.description || ""}</td>
                                <td style={{ ...pS.td, textAlign: "center" }}>{r.unit}</td>
                                <td style={{ ...pS.td, textAlign: "right" }}>{r.qty}</td>
                                {!isDO && (
                                    <>
                                        <td style={{ ...pS.td, textAlign: "right" }}>
                                            {Number(r.unitPrice || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td style={{ ...pS.td, textAlign: "right", fontWeight: "bold" }}>
                                            {Number(round2((r.qty || 0) * (r.unitPrice || 0))).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))
                    }
                    {emptyRows.map((_, i) => (
                        <tr key={`empty-${i}`}>
                            <td style={{ ...pS.td, height: "32px" }}>&nbsp;</td>
                            <td style={pS.td} /><td style={pS.td} /><td style={pS.td} />
                            {!isPV && <td style={pS.td} />}
                            {!isDO && !isPV && <><td style={pS.td} /><td style={pS.td} /></>}
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* ── Totals & remarks ── */}
            <section style={{ width: "100%", marginTop: "20px", borderTop: "1px solid #000" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={pS.remarks}>
                        <div style={{ fontWeight: "bold", fontSize: 10, textTransform: "uppercase", marginBottom: 5 }}>
                            REMARKS / NOTES
                        </div>
                        <div style={{ whiteSpace: "pre-line" }}>{data.notes || "Thank you."}</div>
                    </div>

                    {!isDO && (
                        <div style={{ width: "240px", flexShrink: 0, paddingTop: "10px" }}>
                            {isPV ? (
                                <div style={pS.grandTotalRow}>
                                    <span>TOTAL AMOUNT PAID</span>
                                    <span style={{ textAlign: "right" }}>
                                        {Number(displayGrandTotal).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <div style={pS.totalRow}>
                                        <span style={pS.totalLabel}>Subtotal</span>
                                        <span style={{ textAlign: "right" }}>
                                            {Number(displaySubtotal).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                    {taxRate > 0 && (
                                        <div style={pS.totalRow}>
                                            <span style={pS.totalLabel}>SST ({taxRate}%)</span>
                                            <span style={{ textAlign: "right" }}>
                                                {Number(displayTaxAmount).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    )}
                                    <div style={pS.grandTotalRow}>
                                        <span>
                                            {docType === "Credit Note"
                                                ? `CREDIT AMOUNT (${data.currency === "MYR" ? "RM" : data.currency})`
                                                : docType === "Debit Note"
                                                    ? `DEBIT AMOUNT (${data.currency === "MYR" ? "RM" : data.currency})`
                                                    : `GRAND TOTAL (${data.currency === "MYR" ? "RM" : data.currency})`}
                                        </span>
                                        <span style={{ textAlign: "right" }}>
                                            {Number(displayGrandTotal).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* ── Signature section ── */}
            <section style={pS.signatureSection}>
                <div style={pS.signatureBox}>
                    <div>PREPARED BY</div>
                    <div style={pS.signatureLine}>( Company Stamp &amp; Signature )</div>
                </div>
                <div style={pS.signatureBox}>
                    <div>RECEIVED / APPROVED BY</div>
                    <div style={pS.signatureLine}>( Authorized Signatory )</div>
                </div>
            </section>

            <footer style={pS.footer}>
                This is a computer-generated document. No signature is required.
            </footer>
        </div>
    );
});